import React from "react";
import { Box, Typography } from "@mui/material";
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import logoImage from "../../assets/images/logo.png";

function PoliticaPrivacidad() {
  const pageTitle = "Política de Privacidad - Clinifica";
  const pageDescription = "Política de Privacidad de Clinifica Chile SpA. Información sobre cómo recopilamos, usamos y protegemos tus datos personales.";

  const renderClinicaPillButton = () => {
    const handlePillClick = () => {
      window.location.href = "https://www.clinifica.com/pro";
    };

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 4,
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            mb: 1.5,
            color: "#626262",
            fontSize: { md: "16px", xs: "14px" },
          }}
        >
          ¿Eres profesional de salud independiente?
        </Typography>

        <Box
          onClick={handlePillClick}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "white",
            borderRadius: "50px",
            px: 3,
            py: 1,
            mb: 4,
            border: "1px solid #e0e0e0",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
              cursor: "pointer",
            },
            transition: "box-shadow 0.3s ease",
          }}
        >
          <Box
            sx={{
              mr: 1.5,
              color: "#4285F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0,
            }}
          >
            <img
              src="/clinifica_logo.svg"
              alt="logo"
              style={{
                width: isMobile ? "27px" : "27px",
              }}
            />
          </Box>

          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { md: "16px", xs: "14px" }, mb: 0 }}>
            Crea tu perfil gratis en{" "}
            <Box
              component="span"
              sx={{ fontFamily: "Quicksand-Bold", color: "#4285F4" }}
            >
              Clinifica
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />

      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 0,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: "Quicksand-Bold",
            mb: 2,
            textAlign: "center",
            fontSize: { md: "28px", xs: "22px" },
          }}
        >
          POLÍTICA DE PRIVACIDAD
        </Typography>

        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            mb: 1,
            textAlign: "center",
            fontSize: { md: "16px", xs: "14px" },
            color: "#626262",
          }}
        >
          CLINIFICA CHILE SPA
        </Typography>

        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            mb: 3,
            textAlign: "center",
            fontSize: { md: "14px", xs: "12px" },
            color: "#626262",
          }}
        >
          Última actualización: 29 de septiembre de 2025
        </Typography>

        <Box
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#09090a",
            lineHeight: "1.6",
            textAlign: "left",
            "& h2": {
              fontFamily: "Quicksand-Bold",
              fontSize: { md: "20px", xs: "18px" },
              mt: 3,
              mb: 2,
            },
            "& h3": {
              fontFamily: "Quicksand-SemiBold",
              fontSize: { md: "18px", xs: "16px" },
              mt: 2,
              mb: 1.5,
            },
            "& p": {
              mb: 2,
            },
            "& ul, & ol": {
              pl: 3,
              mb: 2,
            },
            "& li": {
              mb: 0.5,
            },
            "& a": {
              color: "#1976D2",
              textDecoration: "underline",
              "&:hover": {
                color: "#115293",
              },
            },
            "& strong": {
              fontFamily: "Quicksand-Bold",
            },
          }}
        >
          <h2>1. INTRODUCCIÓN</h2>
          <p>
            En Clinifica Chile SpA (RUT 77.729.376-1), domiciliada en Eulogio Sánchez 065, Providencia, Santiago, valoramos y respetamos profundamente su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos tu información personal al usar nuestra plataforma de gestión para profesionales de la salud independientes en Chile.
          </p>
          <p>Esta política se aplica a:</p>
          <ul>
            <li>Sitio web: clinifica.com</li>
            <li>Portal profesional: pro.clinifica.com</li>
            <li>Perfiles web profesionales</li>
            <li>Buscador de profesionales</li>
            <li>Aplicación móvil (cuando esté disponible)</li>
          </ul>
          <p>
            Al usar Clinifica, aceptas esta política. Si no estás de acuerdo, no uses nuestros servicios. Esta Política debe leerse junto con nuestros Términos y Condiciones de Uso.
          </p>

          <h2>2. INFORMACIÓN QUE RECOPILAMOS</h2>

          <h3>2.1 Del Usuario Profesional</h3>
          <p>Cuando te registras como profesional de la salud, recopilamos:</p>
          <p><strong>Datos personales:</strong></p>
          <ul>
            <li>Nombre completo, RUT, fecha de nacimiento</li>
            <li>Correo electrónico, teléfono, dirección de consulta</li>
            <li>Fotografía de perfil (opcional)</li>
          </ul>
          <p><strong>Información profesional:</strong></p>
          <ul>
            <li>Profesión, especialidad y subespecialidad</li>
            <li>Número de registro ante Superintendencia de Salud</li>
            <li>Títulos, certificaciones, experiencia (opcional)</li>
            <li>Descripción de servicios ofrecidos</li>
          </ul>
          <p><strong>Datos de cuenta:</strong></p>
          <ul>
            <li>Credenciales de acceso (correo y contraseña cifrada)</li>
            <li>Preferencias, zona horaria, idioma</li>
          </ul>
          <p><strong>Información bancaria y tributaria:</strong></p>
          <ul>
            <li>Datos bancarios para transferencias</li>
            <li>Información fiscal para boletas electrónicas SII</li>
            <li>Historial de transacciones</li>
          </ul>

          <h3>2.2 De Pagos y Facturación</h3>
          <ul>
            <li>Información de tarjetas (procesada por Transbank/Stripe)</li>
            <li>Historial de suscripciones y renovaciones</li>
            <li>Transacciones y comisiones</li>
            <li>Datos requeridos por el SII</li>
          </ul>

          <h3>2.3 De Uso y Analítica</h3>
          <ul>
            <li>Páginas visitadas y funcionalidades usadas</li>
            <li>Dispositivo, navegador, sistema operativo</li>
            <li>Dirección IP, ubicación general (ciudad/región)</li>
            <li>Sesiones y patrones de uso</li>
            <li>Datos de Google Calendar (si autorizas sincronización)</li>
          </ul>

          <h3>2.4 De Pacientes (Responsabilidad del Profesional)</h3>
          <p><strong>Importante:</strong> Tú eres el responsable del tratamiento de los datos de tus pacientes. Clinifica actúa como encargado del tratamiento.</p>
          <p>Los profesionales pueden ingresar:</p>
          <ul>
            <li>Datos personales básicos (nombre, RUT, contacto, dirección)</li>
            <li>Información médica y clínica (datos sensibles): diagnósticos, tratamientos, antecedentes médicos, notas clínicas, resultados de exámenes, imágenes médicas</li>
            <li>Datos específicos según especialidad (antropometría, evaluaciones psicológicas, planes de tratamiento, etc.)</li>
            <li>Historial de citas, pagos, bonos FONASA</li>
          </ul>
          <p>Es tu responsabilidad:</p>
          <ul>
            <li>Obtener consentimiento informado de tus pacientes (Ley N° 20.584)</li>
            <li>Informarles que usas Clinifica para gestionar sus datos</li>
            <li>Cumplir con confidencialidad médica</li>
          </ul>

          <h3>2.5 De Comunicaciones</h3>
          <ul>
            <li>Correos electrónicos enviados y recibidos</li>
            <li>Mensajes por WhatsApp o chat de soporte</li>
            <li>Consultas, reclamos o solicitudes</li>
            <li>Feedback sobre el servicio</li>
          </ul>

          <h2>3. CÓMO USAMOS LA INFORMACIÓN</h2>
          <p><strong>Prestación del servicio:</strong></p>
          <ul>
            <li>Proporcionar acceso al Portal de Gestión</li>
            <li>Habilitar Perfil Web Profesional y Buscador</li>
            <li>Procesar y almacenar datos de pacientes de forma segura</li>
            <li>Gestionar agenda, recordatorios y notificaciones</li>
            <li>Procesar pagos y transferirlos a tu cuenta</li>
          </ul>
          <p><strong>Gestión de cuenta:</strong></p>
          <ul>
            <li>Procesar suscripciones y renovaciones</li>
            <li>Emitir boletas electrónicas ante SII</li>
            <li>Tramitar bonos FONASA (cuando aplique)</li>
            <li>Aplicar comisiones según contrato</li>
          </ul>
          <p><strong>Seguridad:</strong></p>
          <ul>
            <li>Verificar identidad y registro profesional</li>
            <li>Detectar fraudes y actividades sospechosas</li>
            <li>Proteger integridad de la plataforma</li>
          </ul>
          <p><strong>Mejora del servicio:</strong></p>
          <ul>
            <li>Analizar uso para optimizar funcionalidades</li>
            <li>Desarrollar nuevas características</li>
            <li>Resolver problemas técnicos</li>
          </ul>
          <p><strong>Soporte y comunicación:</strong></p>
          <ul>
            <li>Responder consultas y dar asistencia</li>
            <li>Enviar notificaciones importantes</li>
            <li>Informar cambios en términos y políticas</li>
          </ul>
          <p><strong>Cumplimiento legal:</strong></p>
          <ul>
            <li>Cumplir Ley N° 19.628 y Ley N° 20.584</li>
            <li>Responder requerimientos de autoridades</li>
            <li>Mantener registros según normativa sanitaria y tributaria</li>
          </ul>

          <h2>4. BASE LEGAL PARA EL PROCESAMIENTO</h2>
          <p>Procesamos tus datos basándonos en la Ley N° 19.628 sobre Protección de la Vida Privada:</p>
          <ul>
            <li><strong>Consentimiento:</strong> Al registrarte y aceptar estos términos</li>
            <li><strong>Ejecución de contrato:</strong> Para prestarte el servicio contratado</li>
            <li><strong>Obligación legal:</strong> Boletas SII, registros contables, normativa sanitaria</li>
            <li><strong>Interés legítimo:</strong> Mejora del servicio, prevención de fraude, seguridad</li>
          </ul>
          <p><strong>Responsabilidad del Usuario Profesional:</strong> Eres el responsable del tratamiento de datos de tus pacientes. Debes obtener consentimiento informado y cumplir la normativa aplicable. Clinifica solo actúa como encargado siguiendo tus instrucciones.</p>

          <h2>5. DATOS SENSIBLES DE SALUD</h2>
          <p>Los datos de salud son especialmente protegidos según Ley N° 19.628.</p>
          <p>Garantías que aplicamos:</p>
          <ul>
            <li>Cifrado robusto (TLS 1.3, AES-256)</li>
            <li>Acceso restringido: solo tú ves los datos de tus pacientes</li>
            <li>Segregación completa por profesional</li>
            <li>Trazabilidad de todos los accesos</li>
            <li>Cumplimiento Ley N° 20.584</li>
          </ul>

          <h3>5.1 Datos de Menores de Edad</h3>
          <p>Clinifica no recopila directamente datos de menores de 18 años. Si atiendes menores, es tu responsabilidad exclusiva obtener consentimiento de padres o tutores legales conforme a la ley.</p>

          <h3>5.2 Prohibición de Discriminación</h3>
          <p>Nos comprometemos a no usar datos sensibles para fines discriminatorios (art. 10 Ley 19.628).</p>

          <h2>6. SEGURIDAD Y ALMACENAMIENTO</h2>
          <p>Implementamos medidas de seguridad técnicas, administrativas y físicas:</p>
          <p><strong>Técnicas:</strong></p>
          <ul>
            <li>Cifrado TLS 1.3 (datos en tránsito) y AES-256 (datos en reposo)</li>
            <li>Autenticación segura con opción 2FA</li>
            <li>Firewalls y sistemas anti-intrusión</li>
            <li>Backups automáticos cifrados</li>
            <li>Monitoreo 24/7</li>
            <li>Control de acceso estricto (RLS - Row Level Security)</li>
          </ul>
          <p><strong>Administrativas:</strong></p>
          <ul>
            <li>Personal capacitado en protección de datos</li>
            <li>Acuerdos de confidencialidad</li>
            <li>Políticas de gestión de incidentes</li>
            <li>Auditorías regulares</li>
          </ul>
          <p><strong>Almacenamiento:</strong> Datos almacenados en infraestructura cloud certificada (ISO 27001, SOC 2 Type II) con redundancia geográfica.</p>
          <p><strong>Tu responsabilidad:</strong></p>
          <ul>
            <li>Mantener contraseña segura y confidencial</li>
            <li>No compartir tu cuenta</li>
            <li>Cerrar sesión en dispositivos compartidos</li>
            <li>Reportar actividades sospechosas</li>
          </ul>
          <p><strong>Reconocimiento:</strong> Ningún sistema es 100% seguro. Te notificaremos inmediatamente en caso de brecha de seguridad conforme a la ley.</p>

          <h2>7. COMPARTICIÓN CON TERCEROS</h2>
          <p><strong>NO vendemos ni comercializamos tus datos.</strong></p>

          <h3>7.1 Proveedores Esenciales</h3>
          <p>Solo compartimos con:</p>
          <ul>
            <li>Transbank/Stripe: Procesamiento seguro de pagos</li>
            <li>SII: Emisión de boletas electrónicas</li>
            <li>FONASA: Tramitación de bonos (cuando aplique)</li>
            <li>Google Analytics: Análisis de uso (datos agregados)</li>
            <li>Proveedores de hosting: Almacenamiento seguro</li>
          </ul>
          <p>Todos están obligados por contratos de confidencialidad y solo pueden usar datos para propósitos específicos autorizados.</p>

          <h3>7.2 Obligaciones Legales</h3>
          <p>Podemos divulgar información cuando sea legalmente requerido:</p>
          <ul>
            <li>Órdenes judiciales</li>
            <li>Superintendencia de Salud</li>
            <li>Investigaciones de autoridades competentes</li>
          </ul>
          <p>Solo divulgaremos lo estrictamente necesario y te notificaremos cuando sea legalmente permitido.</p>

          <h3>7.3 Transferencias Corporativas</h3>
          <p>En caso de fusión, adquisición o venta de Clinifica:</p>
          <ul>
            <li>Te notificaremos con anticipación</li>
            <li>El nuevo propietario debe respetar esta política</li>
            <li>Tendrás derecho a eliminar tus datos antes</li>
          </ul>

          <h3>7.4 Datos Anonimizados</h3>
          <p>Podemos compartir estadísticas agregadas que no identifican personas individuales para investigación, tendencias de salud o mejoras.</p>

          <h2>8. TUS DERECHOS DE PRIVACIDAD</h2>
          <p>Según la Ley N° 19.628, tienes derecho a:</p>
          <ul>
            <li><strong>Acceso:</strong> Solicitar copia de tu información (respuesta en 2 días hábiles)</li>
            <li><strong>Rectificación:</strong> Corregir datos incorrectos o desactualizados</li>
            <li><strong>Cancelación:</strong> Eliminar tu cuenta y datos (salvo obligaciones legales de conservación)</li>
            <li><strong>Portabilidad:</strong> Recibir tus datos en formato exportable (CSV, JSON, PDF)</li>
            <li><strong>Oposición:</strong> Oponerte a ciertos usos de tus datos cuando se basen en interés legítimo</li>
            <li><strong>Limitación:</strong> Solicitar que limitemos el procesamiento mientras verificamos exactitud o legalidad</li>
            <li><strong>Revocar consentimiento:</strong> En cualquier momento (no afecta tratamiento previo)</li>
          </ul>

          <h3>8.1 Derechos Específicos sobre Datos de Salud (Ley N° 20.584)</h3>
          <p>Como profesional, debes garantizar a tus pacientes:</p>
          <ul>
            <li>Confidencialidad de información clínica</li>
            <li>Acceso a su ficha clínica</li>
            <li>Rectificación de información médica</li>
            <li>Entrega de copias cuando sean solicitadas</li>
          </ul>

          <h3>8.2 Cómo Ejercer Tus Derechos</h3>
          <p><strong>Contacto:</strong></p>
          <p>📩 felipe@clinifica.com o pablo@clinifica.com</p>
          <p>📲 WhatsApp: +56 9 2080 9101</p>
          <p><strong>Información requerida:</strong></p>
          <ul>
            <li>Nombre completo y RUT</li>
            <li>Correo asociado a la cuenta</li>
            <li>Descripción del derecho a ejercer</li>
            <li>Copia de cédula de identidad</li>
          </ul>
          <p><strong>Plazos:</strong></p>
          <ul>
            <li>Solicitudes simples: 2 días hábiles</li>
            <li>Solicitudes complejas: 15 días hábiles</li>
            <li>Extensión excepcional: Hasta 30 días con justificación</li>
          </ul>
          <p><strong>Sin costo:</strong> El ejercicio de estos derechos es gratuito.</p>

          <h3>8.3 Derecho a Reclamo</h3>
          <p>Si consideras vulnerados tus derechos, puedes:</p>
          <ul>
            <li>Presentar reclamo ante Clinifica</li>
            <li>Acudir a SERNAC (derechos del consumidor)</li>
            <li>Recurso judicial: Acción de protección ante Corte de Apelaciones (art. 16 Ley 19.628)</li>
            <li>Superintendencia de Salud (temas sanitarios)</li>
          </ul>

          <h2>9. RETENCIÓN Y ELIMINACIÓN DE DATOS</h2>
          <p>Conservamos información solo el tiempo necesario.</p>

          <h3>9.1 Períodos de Retención</h3>
          <ul>
            <li><strong>Cuenta activa:</strong> Mientras la suscripción esté vigente</li>
            <li><strong>Tras cancelación voluntaria:</strong> 90 días en estado inactivo (período de gracia), luego eliminación permanente</li>
            <li><strong>Datos de facturación:</strong> 7 años (Código Tributario chileno)</li>
            <li><strong>Datos de pacientes:</strong> Según determinen las obligaciones profesionales del Usuario. Recomendación: mínimo 15 años (estándares de historia clínica en Chile). Puedes solicitar eliminación asumiendo responsabilidad legal</li>
            <li><strong>Datos de analítica:</strong> 6-12 meses</li>
            <li><strong>Soporte y comunicaciones:</strong> 2-3 años</li>
          </ul>

          <h3>9.2 Eliminación Tras Cancelación</h3>
          <ul>
            <li><strong>Fase 1 (0-30 días):</strong> Cuenta inactiva, datos recuperables</li>
            <li><strong>Fase 2 (31-90 días):</strong> Archivo temporal, posible reactivación</li>
            <li><strong>Fase 3 (después de 90 días):</strong> Eliminación definitiva e irreversible de datos de perfil, configuraciones y datos de pacientes (salvo que los hayas exportado)</li>
          </ul>
          <p>Se conservan solo: datos fiscales (obligación legal 7 años), registros de auditoría necesarios, datos anonimizados para estadísticas.</p>
          <p>Puedes solicitar eliminación inmediata sin esperar 90 días.</p>

          <h3>9.3 Eliminación Segura</h3>
          <p>Cuando eliminamos datos:</p>
          <ul>
            <li>Eliminación lógica y física</li>
            <li>Sobrescritura segura en servidores</li>
            <li>Eliminación de backups</li>
            <li>Proceso irreversible</li>
          </ul>

          <h2>10. TRANSFERENCIAS INTERNACIONALES</h2>
          <p>Los datos pueden almacenarse en servidores ubicados fuera de Chile (principalmente USA y UE) con proveedores certificados.</p>
          <p><strong>Garantías implementadas:</strong></p>
          <ul>
            <li>Cláusulas Contractuales Estándar</li>
            <li>Proveedores certificados: ISO 27001, SOC 2, GDPR-compliant</li>
            <li>Cifrado de datos</li>
            <li>Contratos con obligaciones de protección</li>
          </ul>
          <p><strong>Tus derechos:</strong></p>
          <ul>
            <li>Conocer países de destino</li>
            <li>Información sobre garantías implementadas</li>
            <li>Oponerte a transferencias específicas</li>
            <li>Obtener copia de cláusulas contractuales</li>
          </ul>

          <h2>11. COOKIES Y TECNOLOGÍAS SIMILARES</h2>
          <p>Usamos cookies para mejorar tu experiencia y analizar uso de la plataforma.</p>

          <h3>11.1 Tipos de Cookies</h3>
          <p><strong>Esenciales (obligatorias):</strong></p>
          <ul>
            <li>Gestión de sesiones y autenticación</li>
            <li>Seguridad y prevención de fraudes</li>
            <li>No se pueden desactivar</li>
          </ul>
          <p><strong>Analíticas:</strong></p>
          <ul>
            <li>Google Analytics para entender uso</li>
            <li>Métricas de rendimiento</li>
            <li>Pueden desactivarse sin afectar funcionalidad</li>
          </ul>
          <p><strong>Funcionales:</strong></p>
          <ul>
            <li>Recordar preferencias (idioma, configuraciones)</li>
            <li>Chat de soporte</li>
            <li>Pueden desactivarse con posible pérdida de personalización</li>
          </ul>

          <h3>11.2 Control de Cookies</h3>
          <p><strong>Desde la plataforma:</strong> Panel de configuración de cookies donde puedes aceptar, rechazar o personalizar por categoría.</p>
          <p><strong>Desde tu navegador:</strong></p>
          <ul>
            <li>Chrome: Configuración → Privacidad → Cookies</li>
            <li>Firefox: Opciones → Privacidad → Cookies</li>
            <li>Safari: Preferencias → Privacidad → Cookies</li>
            <li>Edge: Configuración → Privacidad → Cookies</li>
          </ul>
          <p><strong>Tecnologías adicionales:</strong></p>
          <ul>
            <li>Balizas web en emails (medir efectividad)</li>
            <li>Local Storage para datos no sensibles</li>
            <li>Fingerprinting solo para prevención de fraudes (no tracking publicitario)</li>
          </ul>

          <h2>12. INCIDENTES DE SEGURIDAD</h2>
          <p>En caso de brecha de seguridad que afecte datos personales:</p>
          <p><strong>Nos comprometemos a:</strong></p>
          <ul>
            <li>Notificarte dentro de 2 días hábiles desde que tengamos conocimiento</li>
            <li>Informar: (i) tipo de incidente, (ii) datos afectados, (iii) impacto posible, (iv) medidas correctivas</li>
            <li>Cooperar con autoridades competentes</li>
            <li>Implementar medidas adicionales de protección</li>
          </ul>
          <p><strong>Tu obligación:</strong> Si detectas un incidente relacionado con tu cuenta o datos de pacientes, debes notificarnos inmediatamente.</p>

          <h2>13. DELEGADO DE PROTECCIÓN DE DATOS</h2>
          <p>Clinifica puede designar un Delegado de Protección de Datos (DPD) cuando legalmente corresponda, quien actuará como canal de comunicación con titulares y autoridades.</p>
          <p>Su contacto será publicado en la plataforma cuando se designe.</p>

          <h2>14. INFORMACIÓN CONFIDENCIAL</h2>
          <p>Durante el uso de la plataforma, ambas partes pueden acceder a información confidencial.</p>
          <p><strong>Nos comprometimos a:</strong></p>
          <ul>
            <li>Mantener en estricta confidencialidad datos no públicos</li>
            <li>No usar información para fines distintos al servicio</li>
            <li>No divulgar a terceros sin autorización</li>
          </ul>
          <p><strong>Tú te comprometes a:</strong></p>
          <ul>
            <li>Resguardar datos de pacientes (confidencialidad médica)</li>
            <li>Mantener información de Clinifica en reserva</li>
            <li>No usar información para competir o eludir la plataforma</li>
          </ul>
          <p>Esta obligación permanece vigente por 2 años tras terminación del contrato.</p>
          <p><strong>No es confidencial:</strong></p>
          <ul>
            <li>Información pública</li>
            <li>Información conocida previamente de forma lícita</li>
            <li>Información que debe divulgarse por orden de autoridad</li>
            <li>Datos anonimizados sin identificación personal</li>
          </ul>

          <h2>15. CAMBIOS A ESTA POLÍTICA</h2>
          <p>Podemos actualizar esta política para reflejar cambios en prácticas, tecnología o requisitos legales.</p>
          <p><strong>Te notificaremos:</strong></p>
          <ul>
            <li>Por correo electrónico</li>
            <li>Aviso en la plataforma</li>
            <li>Notificación al iniciar sesión</li>
          </ul>
          <p>Con al menos 15 días de anticipación antes de su entrada en vigor.</p>
          <p>Te recomendamos revisar esta política periódicamente.</p>

          <h2>16. CONTACTO Y RECLAMOS</h2>
          <p>Para consultas sobre privacidad:</p>
          <p>📩 Correo: felipe@clinifica.com y pablo@clinifica.com</p>
          <p>📲 WhatsApp: +56 9 2080 9101</p>
          <p>📍 Dirección: Eulogio Sánchez 065, Providencia, Santiago, Chile</p>
          <p>Tiempo de respuesta: 2 días hábiles para solicitudes simples, hasta 30 días para complejas.</p>

          <h3>16.1 Reclamos ante Autoridades</h3>
          <p>Si no estás satisfecho con nuestra respuesta, puedes presentar reclamo ante:</p>
          <ul>
            <li>SERNAC: Para temas de consumidor</li>
            <li>Corte de Apelaciones: Acción de protección (art. 16 Ley 19.628)</li>
            <li>Superintendencia de Salud: Para temas sanitarios</li>
          </ul>

          <h2>17. DISPOSICIONES FINALES</h2>
          <p>Esta Política de Privacidad se rige por las leyes de Chile:</p>
          <ul>
            <li>Ley N° 19.628 (Protección de la Vida Privada)</li>
            <li>Ley N° 20.584 (Derechos en Atención de Salud)</li>
            <li>Código Sanitario y normativa de Superintendencia de Salud</li>
          </ul>
          <p>Al usar Clinifica, reconoces que has leído, entendido y aceptas esta Política de Privacidad.</p>
        </Box>
      </Box>

      {renderClinicaPillButton()}
    </>
  );
}

export default PoliticaPrivacidad;